import AppState from '../app/AppState';
import IPreferredStoreProvider from '../app/data/StoreProviders/IPreferredStoreProvider';
import { IStoreInitializationStrategy, StoreInitializationResult } from '../app/IStoreInitializationStrategy';
import { EmbeddedQueryParser } from '../app/shared/EmbeddedQueryParser';
import { StoreUrlBuilder } from '../app/shared/StoreUrlBuilder';
import { ParsedUrlPath, StoreUrlParser } from '../app/shared/StoreUrlParser';
import { RelativeUrl } from '../app/shared/Url';
import { StoreState } from '../app/StoreState';

export class StoreInitializationStrategy implements IStoreInitializationStrategy {
  async tryInitialize(
    path: string,
    search: string,
    appState: AppState,
    preferredStoreProvider: IPreferredStoreProvider,
  ): Promise<StoreInitializationResult> {
    const parsedUrlPath = await StoreInitializationStrategy.parseUrlPath(path, appState);
    const query = RelativeUrl.parseQuery(search);

    const countryCode = StoreInitializationStrategy.getInitialCountryCode(parsedUrlPath, preferredStoreProvider, query);
    const languageCode = StoreInitializationStrategy.getInitialLanguageCode(parsedUrlPath, preferredStoreProvider);
    const storeId = StoreInitializationStrategy.getInitialStoreId(parsedUrlPath, preferredStoreProvider, query);
    const shouldSkipStoreAndLanguageResolution = StoreInitializationStrategy.shouldSkipStrategy(query);

    const store = await appState.loadStore(storeId, countryCode, languageCode, shouldSkipStoreAndLanguageResolution);

    if (shouldSkipStoreAndLanguageResolution) {
      return { redirected: false, purePath: parsedUrlPath.pure };
    }

    if (StoreInitializationStrategy.isRedirectionToDifferentStoreRequired(parsedUrlPath, store)) {
      if (!StoreInitializationStrategy.shouldPreserveStore(query)) {
        query.set('store', 'auto');
      }

      StoreInitializationStrategy.redirectToStore(appState, parsedUrlPath, store, query);

      return { redirected: true };
    }

    if (StoreInitializationStrategy.isInitialStoreSelectionRequired(query, store, countryCode)) {
      appState.initialStoreSelectionModal.open();
    }

    return { redirected: false, purePath: parsedUrlPath.pure };
  }

  private static getInitialCountryCode(
    path: ParsedUrlPath,
    preferredStoreProvider: IPreferredStoreProvider,
    query: Map<string, string>,
  ) {
    const preferredCountryCode = preferredStoreProvider.getCountryCode();

    if (preferredCountryCode) {
      return preferredCountryCode;
    }

    const internationalCode = 'GLOBAL';
    const isGlobalStoreInPath = StoreInitializationStrategy.isGlobalStoreInUrlPath(path);
    const shouldPreserveStore = StoreInitializationStrategy.shouldPreserveStore(query);

    if (isGlobalStoreInPath && shouldPreserveStore) {
      return internationalCode;
    }

    return null;
  }

  private static getInitialLanguageCode(path: ParsedUrlPath, preferredStoreProvider: IPreferredStoreProvider) {
    const isGlobalStoreInPath = StoreInitializationStrategy.isGlobalStoreInUrlPath(path);

    if (!isGlobalStoreInPath && path.hasValidLanguage) {
      return path.languageCode;
    }

    return preferredStoreProvider.getLanguageCode();
  }

  private static getInitialStoreId(
    path: ParsedUrlPath,
    preferredStoreProvider: IPreferredStoreProvider,
    query: Map<string, string>,
  ) {
    const isGlobalStoreInPath = StoreInitializationStrategy.isGlobalStoreInUrlPath(path);
    const shouldPreserveStore = StoreInitializationStrategy.shouldPreserveStore(query);

    if (isGlobalStoreInPath && !shouldPreserveStore) {
      return preferredStoreProvider.getStoreId();
    }

    return path.storeId;
  }

  private static isGlobalStoreInUrlPath(path: ParsedUrlPath) {
    const globalStore = 'global';

    if (path.storeId === null) {
      return true;
    }

    return path.storeId === globalStore;
  }

  private static isStoreAutoDetected(query: Map<string, string>) {
    return query.get('store') === 'auto';
  }

  private static shouldPreserveStore(query: Map<string, string>) {
    return query.get('store') === 'preserve';
  }

  private static shouldSkipStrategy(query: Map<string, string>) {
    return query.get('skipStoreInitialization') === 'true';
  }

  private static async parseUrlPath(path: string, appState: AppState) {
    const availableStores = await appState.loadStores();
    const validStorePredicate = (storeCandidate: string) =>
      availableStores.any((x) => x.toLocaleLowerCase() === storeCandidate);

    return StoreUrlParser.parse(path, validStorePredicate);
  }

  private static isRedirectionToDifferentStoreRequired(path: ParsedUrlPath, store: StoreState) {
    const isStoreOrLanguageInPathInvalid = !path.hasValidStore || !path.hasValidLanguage;
    const isStoreDifferentThanRequestedStore = path.storeId !== store.shoppingContext.storeId.toLocaleLowerCase();
    const isLanguageDifferentThanRequestedLanguage =
      path.languageCode !== store.shoppingContext.languageCode.toLocaleLowerCase();

    return (
      isStoreOrLanguageInPathInvalid || isStoreDifferentThanRequestedStore || isLanguageDifferentThanRequestedLanguage
    );
  }

  private static isInitialStoreSelectionRequired(query: Map<string, string>, store: StoreState, countryCode: string) {
    const { id } = store.storeResponse.storeContext.store;
    const embeddedQuery = EmbeddedQueryParser.toModel(query);
    const isGlobalStore = id === 'global';
    const isStoreAutoDetected = StoreInitializationStrategy.isStoreAutoDetected(query);
    const shouldPreserveStore = StoreInitializationStrategy.shouldPreserveStore(query);

    return (isStoreAutoDetected || (isGlobalStore && !shouldPreserveStore)) && !countryCode && !embeddedQuery.embed;
  }

  private static redirectToStore(
    appState: AppState,
    path: ParsedUrlPath,
    store: StoreState,
    query: Map<string, string>,
  ) {
    const redirectionUrl = StoreUrlBuilder.fromContext(store.shoppingContext, path.pure, query).toString();
    appState.navigation.redirect(redirectionUrl);
  }
}
